import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormikValues } from 'formik';
import useUpdateRule from 'Hooks/query-hooks/global-rules/useUpdateRule';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import AdvancedRuleForm from 'Components/AdvancedRuleForm';
import Layout from 'Components/Layout';
import { setGroup } from 'Store';

type UpdateAdvancedRuleLayoutProps = {
    listData: ListApiGetResponse;
    mode: 'edit';
    ruleData: CustomGetRuleAPIResponse;
    selectedRowId?: string;
    parameterData: UseGetParametersResponse | undefined;
};

const UpdateAdvancedRuleLayout = ({
    listData,
    mode,
    ruleData,
    selectedRowId,
    parameterData
}: UpdateAdvancedRuleLayoutProps) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const formRef = React.useRef<FormikValues>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const dispatch = useDispatch();
    const { data: landlordInfo } = useGetLandlordInfo();

    /**
     * @note Checking if priority field is empty. Execution order is a new feature and
     * some Advanced Rules doesn't have Priority field data yet. Making sure the data is available.
     */
    const formInitialValues: AdvancedRuleFormSchema = {
        ...ruleData,
        priority: ruleData.priority ?? 1,
        createdBy: ruleData._formatted?.createdBy,
        createdAt: ruleData._formatted?.createdAt,
        group: [String(ruleData.group)],
        allowOverrides: ruleData.allowOverrides || false,
        isScriptValidated: true
    };

    const onSuccess = (_data: any, { ruleId }: any) => {
        dispatch(setGroup(''));
        addAlert({
            id: 'update-advanced-rule-success',
            content: t('addRuleSuccess', { ruleId }),
            status: 'success'
        });

        history.push(`/global-rules?selectedRowId=${selectedRowId}`);
    };

    const onError = (err: any) => {
        const handleDefault = () => {
            addModal({
                id: 'error-saving-advanced-rule',
                status: 'error',
                title: t('Unable to save rule'),
                content: (
                    <>
                        {err?.data?.error?.message
                            ? t('addRuleErrorSpecific', {
                                  errMsg: err?.data?.error?.message ?? err?.data?.error?.msg?.error
                              })
                            : t('addRuleErrorGeneric')}
                    </>
                ),
                cancelText: t('Close')
            });
        };

        const handleNotFound = () => {
            if (err.response?.data?.message === t('ruleNotFound')) {
                addModal({
                    id: 'update-advanced-rule-error',
                    status: 'warning',
                    title: t('Error Saving Rule'),
                    content: t('Rule Could Not Be Saved'),
                    showCloseIcon: false,
                    showCloseButton: false,
                    closeOnAction: true,
                    onActionText: 'Confirm',
                    onClose: () => {
                        history.replace('/global-rules');
                    }
                });
            } else {
                handleDefault();
            }
        };

        switch (err?.response?.status) {
            case 404:
                handleNotFound();
                break;
            default:
                handleDefault();
                break;
        }

        if (formRef.current) {
            formRef.current.setSubmitting(false);
        }
    };

    const mutation = useUpdateRule(ruleData.id, {
        templateRepositoryId: templateRepositoryId!,
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    return (
        <Layout>
            <AdvancedRuleForm
                mode={mode}
                pageHeader={t('editAdvancedRuleBreadcrumb', { ruleId: ruleData.ruleId })}
                formRef={formRef}
                listData={listData}
                parameterData={parameterData}
                mutation={mutation}
                formInitialValues={formInitialValues}
                repoId={templateRepositoryId!}
                selectedRowId={selectedRowId}
                disabledInputFields={{
                    disableDefinitionChange: !ruleData.canEditDefinition,
                    disableOverrideChange: !ruleData.canChangeOverrideSettings,
                    hideOverrideChange: !landlordInfo?.isLandlord
                }}
                disableDeleteRuleButton={!ruleData?.canDeleteRule?.canDelete}
                isNonTemplatedRule={ruleData?.nonTemplated}
                isRuleDeployed={ruleData?.canDeleteRule?.isRuleDeployed}
            />
        </Layout>
    );
};

export default UpdateAdvancedRuleLayout;
